// Injiziert eine PDF-Outline (Lesezeichen) in eine vom Browser gedruckte PDF.
//
// pdf-lib hat keine High-Level-Outline-API, also bauen wir die /Outlines-
// Objekthierarchie direkt nach PDF-Spec 12.3.3:
//   - Root-Dict { Type: Outlines, First: <first>, Last: <last>, Count: N }
//   - Item-Dict { Title, Parent, Prev?, Next?, First?, Last?, Count?, Dest }
// Dest = [pageRef, /Fit] → Sprung an Seitenanfang, in den View skaliert.
// Count: positiv = aufgeklappt (alle Nachfahren sichtbar zählen),
//        negativ = zugeklappt (Magnitude = direkte Kinder).
import { PDFDocument, PDFHexString, PDFName, PDFNumber } from "pdf-lib";
/**
 * Flache Bookmark-Liste (in Dokument-Reihenfolge) in einen Baum nach `level` umwandeln.
 * Sprünge wie L1 → L3 werden toleriert (L3 wird Kind des letzten L1).
 */
function buildTree(entries) {
    const root = { entry: null, children: [], parent: null };
    // Stack hält den aktuellen "Pfad" — letztes Element ist der nächste Parent-Kandidat.
    const stack = [root];
    for (const e of entries) {
        // Pop alles, das auf gleichem oder tieferem Level liegt als das neue.
        while (stack.length > 1 && (stack[stack.length - 1].entry?.level ?? 0) >= e.level) {
            stack.pop();
        }
        const parent = stack[stack.length - 1];
        const node = { entry: e, children: [], parent };
        parent.children.push(node);
        stack.push(node);
    }
    return root;
}
/**
 * Zählt sichtbare Nachfahren für /Count. Wir klappen L1+L2 standardmäßig auf,
 * L3+L4 zu — das matched die Default-Ansicht im PDF-Viewer.
 */
function visibleDescendantCount(node) {
    let count = node.children.length;
    for (const c of node.children) {
        const lvl = c.entry?.level ?? 99;
        if (lvl <= 1 && c.children.length > 0) {
            // L1 ist aufgeklappt → seine Nachfahren werden sichtbar gezählt
            count += visibleDescendantCount(c);
        }
    }
    return count;
}
export async function injectBookmarks(pdfBytes, entries) {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const ctx = pdfDoc.context;
    const pages = pdfDoc.getPages();
    // Doppelte Einträge entfernen: gleicher Titel + Page direkt hintereinander
    // (kommt vor wenn Paged.js ein Heading auf Seitenende kopiert)
    const cleaned = [];
    for (const e of entries) {
        if (!e.title || e.page < 1 || e.page > pages.length)
            continue;
        const last = cleaned[cleaned.length - 1];
        if (last && last.title === e.title && last.page === e.page && last.level === e.level)
            continue;
        cleaned.push(e);
    }
    if (cleaned.length === 0) {
        // Keine Bookmarks → unverändert speichern
        return pdfDoc.save();
    }
    const root = buildTree(cleaned);
    // Erst alle Dicts anlegen + registrieren (damit wir Refs für Prev/Next setzen können).
    const allNodes = [];
    (function collect(n) {
        for (const c of n.children) {
            const dict = ctx.obj({ Title: PDFHexString.fromText(c.entry.title) });
            c.dict = dict;
            c.ref = ctx.register(dict);
            allNodes.push(c);
            collect(c);
        }
    })(root);
    // Outline-Root
    const outlineDict = ctx.obj({ Type: "Outlines" });
    const outlineRef = ctx.register(outlineDict);
    // Verdrahten: Parent / Prev / Next / First / Last / Count / Dest
    function wire(parentNode, parentRef) {
        const kids = parentNode.children;
        if (kids.length === 0)
            return;
        if (parentNode === root) {
            outlineDict.set(PDFName.of("First"), kids[0].ref);
            outlineDict.set(PDFName.of("Last"), kids[kids.length - 1].ref);
            outlineDict.set(PDFName.of("Count"), PDFNumber.of(visibleDescendantCount(root)));
        }
        else {
            parentNode.dict.set(PDFName.of("First"), kids[0].ref);
            parentNode.dict.set(PDFName.of("Last"), kids[kids.length - 1].ref);
            const expand = (parentNode.entry?.level ?? 99) <= 1;
            const c = visibleDescendantCount(parentNode);
            parentNode.dict.set(PDFName.of("Count"), PDFNumber.of(expand ? c : -kids.length));
        }
        kids.forEach((child, i) => {
            const d = child.dict;
            d.set(PDFName.of("Parent"), parentRef);
            if (i > 0)
                d.set(PDFName.of("Prev"), kids[i - 1].ref);
            if (i < kids.length - 1)
                d.set(PDFName.of("Next"), kids[i + 1].ref);
            // Ziel-Seite: pdf-lib indexiert 0-basiert, BookmarkEntry.page ist 1-basiert
            const pageRef = pages[child.entry.page - 1].ref;
            d.set(PDFName.of("Dest"), ctx.obj([pageRef, PDFName.of("Fit")]));
            wire(child, child.ref);
        });
    }
    wire(root, outlineRef);
    // Outline in den Catalog hängen + PageMode auf "UseOutlines" damit Viewer
    // die Lesezeichen-Sidebar beim Öffnen direkt zeigt.
    pdfDoc.catalog.set(PDFName.of("Outlines"), outlineRef);
    pdfDoc.catalog.set(PDFName.of("PageMode"), PDFName.of("UseOutlines"));
    return pdfDoc.save();
}
